/**
 * Fullscreen pulsing mic overlay shown while voice recording is in progress.
 * Tap anywhere to stop recording.
 */

import { Ionicons } from '@expo/vector-icons';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
  FadeIn,
  FadeOut,
} from 'react-native-reanimated';
import { useEffect } from 'react';

interface VoiceRecordingOverlayProps {
  visible: boolean;
  accentColor: string;
  onStop: () => void;
}

const MIC_SIZE = 80;
const RING_SIZE = 120;

export function VoiceRecordingOverlay({
  visible,
  accentColor,
  onStop,
}: VoiceRecordingOverlayProps) {
  const ringScale = useSharedValue(1);
  const ringOpacity = useSharedValue(0.4);

  useEffect(() => {
    if (visible) {
      ringScale.value = withRepeat(
        withSequence(
          withTiming(1.4, { duration: 800 }),
          withTiming(1, { duration: 800 }),
        ),
        -1,
      );
      ringOpacity.value = withRepeat(
        withSequence(
          withTiming(0.1, { duration: 800 }),
          withTiming(0.4, { duration: 800 }),
        ),
        -1,
      );
    } else {
      ringScale.value = 1;
      ringOpacity.value = 0.4;
    }
  }, [visible, ringScale, ringOpacity]);

  const ringStyle = useAnimatedStyle(() => ({
    transform: [{ scale: ringScale.value }],
    opacity: ringOpacity.value,
  }));

  if (!visible) return null;

  return (
    <Animated.View
      style={styles.overlay}
      entering={FadeIn.duration(200)}
      exiting={FadeOut.duration(200)}
    >
      <TouchableOpacity
        style={styles.touchArea}
        activeOpacity={1}
        onPress={onStop}
        accessibilityLabel="Tap to stop recording"
      >
        <View style={styles.center}>
          <Animated.View
            style={[
              styles.ring,
              { backgroundColor: accentColor },
              ringStyle,
            ]}
          />
          <View style={[styles.micCircle, { backgroundColor: accentColor }]}>
            <Ionicons name="mic" size={36} color="#FFFFFF" />
          </View>
        </View>
      </TouchableOpacity>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 100,
  },
  touchArea: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  center: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  ring: {
    position: 'absolute',
    width: RING_SIZE,
    height: RING_SIZE,
    borderRadius: RING_SIZE / 2,
  },
  micCircle: {
    width: MIC_SIZE,
    height: MIC_SIZE,
    borderRadius: MIC_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
